import { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://<api-id>.execute-api.us-east-1.amazonaws.com/prod';

const MODES = {
  LOCAL_PC: {
    label:    'Local PC',
    icon:     '🖥️',
    color:    '#2980b9',
    topics:   'biw/local/...',
    clientId: 'BIW_Fixture_Local',
    desc:     'Unity running on developer machine. Use for design validation and development.',
  },
  CLOUD_EC2: {
    label:    'Cloud EC2',
    icon:     '☁️',
    color:    '#27ae60',
    topics:   'biw/cloud/...',
    clientId: 'BIW_Fixture_Cloud',
    desc:     'Unity running on AWS EC2 (t3.small telemetry + g4dn.xlarge demo). Use for production demos.',
  },
};

export default function ModeIndicator({ session }) {
  const [activeMode, setActiveMode] = useState('LOCAL_PC');
  const [saving,     setSaving]     = useState(false);
  const [expanded,   setExpanded]   = useState(false);

  // Load saved mode on mount
  useEffect(() => {
    if (!session?.email) return;
    axios.get(`${API_BASE}/api/config?email=${encodeURIComponent(session.email)}&key=datasource_mode.json`)
      .then(({ data }) => { if (data.data?.mode) setActiveMode(data.data.mode); })
      .catch(() => {});
  }, [session]);

  const switchMode = async (mode) => {
    if (mode === activeMode) return;
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/config`, {
        email:     session.email,
        configKey: 'datasource_mode.json',
        data:      { mode },
      });
      setActiveMode(mode);
    } catch (e) {
      alert('Failed to save mode. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const m = MODES[activeMode];

  return (
    <div style={{ position: 'relative' }}>
      {/* Compact badge in nav */}
      <button onClick={() => setExpanded(e => !e)} style={s.badge(m.color)}>
        {m.icon} {m.label} {expanded ? '▲' : '▼'}
      </button>

      {/* Dropdown panel */}
      {expanded && (
        <div style={s.panel}>
          <p style={s.panelTitle}>Data Source Mode</p>
          <p style={s.panelHint}>
            Controls which MQTT topics Unity publishes to.<br />
            Prevents local PC and cloud EC2 from writing to the same SiteWise asset.
          </p>

          {Object.entries(MODES).map(([key, cfg]) => (
            <div key={key}
              onClick={() => !saving && switchMode(key)}
              style={{ ...s.modeRow, borderColor: activeMode === key ? cfg.color : '#ddd',
                       background: activeMode === key ? cfg.color + '11' : '#fff',
                       cursor: saving ? 'wait' : 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{cfg.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: activeMode === key ? cfg.color : '#333' }}>
                    {cfg.label}
                    {activeMode === key && <span style={s.activeBadge(cfg.color)}>ACTIVE</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{cfg.desc}</div>
                </div>
              </div>
              <div style={s.techInfo}>
                <code style={s.code}>clientId: {cfg.clientId}</code>
                <code style={s.code}>topics: {cfg.topics}</code>
              </div>
            </div>
          ))}

          <div style={s.instruction}>
            <strong>To apply in Unity:</strong> open the <code>BIW_DataSourceConfig</code> asset
            and set <code>mode</code> to match the selection above, then press Play.
          </div>

          {saving && <p style={{ color: '#888', fontSize: 12, margin: '8px 0 0' }}>Saving...</p>}
        </div>
      )}
    </div>
  );
}

const s = {
  badge:       (color) => ({ background: color, color: '#fff', border: 'none', borderRadius: 4,
                              padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }),
  panel:       { position: 'absolute', top: 36, right: 0, width: 420, background: '#fff',
                 border: '1px solid #ddd', borderRadius: 8, padding: 16, zIndex: 1000,
                 boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
  panelTitle:  { fontWeight: 700, fontSize: 14, margin: '0 0 4px', color: '#333' },
  panelHint:   { fontSize: 12, color: '#888', margin: '0 0 12px', lineHeight: 1.5 },
  modeRow:     { border: '2px solid', borderRadius: 6, padding: '10px 12px', marginBottom: 8,
                 transition: 'all 0.15s' },
  activeBadge: (color) => ({ marginLeft: 8, background: color, color: '#fff', fontSize: 10,
                              padding: '1px 6px', borderRadius: 8, fontWeight: 700 }),
  techInfo:    { display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' },
  code:        { background: '#f4f4f4', padding: '1px 5px', borderRadius: 3,
                 fontFamily: 'monospace', fontSize: 10, color: '#555' },
  instruction: { background: '#fffbe6', border: '1px solid #f0c040', borderRadius: 4,
                 padding: '8px 10px', fontSize: 12, marginTop: 8, lineHeight: 1.5 },
};
